import React, { useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import SphereLogo from '../../media/sphereFullLogo.png';
import {
  dismissUpdateAvailable,
  getAppUpdateState,
  subscribeToAppUpdateState,
} from '../../services/app-update-state';
import DialogWrapperWithClose from '@common/components/General/DialogWrapperWithClose';
import SphereLoginBackdrop from '../../media/sphereLoginBackdrop.png';

const useStyles = makeStyles()((theme) => ({
  sphereLogo: {
    width: `min(${theme.spacing(48)}, 100%)`,
  },
  sphereLoginBackdrop: {
    width: '100vw',
    height: '100vh',
  },
}));

const AppUpdateDialog: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const { required, availableVersion, storeUrl } = useSyncExternalStore(
    subscribeToAppUpdateState,
    getAppUpdateState,
  );

  const openStore = (url: string) => {
    window.open(url, '_system');
  };

  if (required) {
    return (
      <React.Fragment>
        <img src={SphereLoginBackdrop} alt="" className={classes.sphereLoginBackdrop} />
        <Dialog maxWidth="sm" open={true}>
          <DialogContent>
            <Stack alignItems="center" spacing={6} maxWidth="sm">
              <img src={SphereLogo} alt="Sphere" className={classes.sphereLogo} />
              <Stack spacing={4} width="100%">
                <Stack>
                  <Typography variant="h6" textAlign="center">{t('app-update-required-title')}</Typography>
                  <Typography variant="body2" color="text.secondary">{t('app-update-required-text')}</Typography>
                </Stack>
                {required.updateUrl && (
                  <Button variant="contained" size="large" fullWidth onClick={() => openStore(required.updateUrl!)}>
                    {t('button-update-now')}
                  </Button>
                )}
              </Stack>
            </Stack>
          </DialogContent>
        </Dialog>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <DialogWrapperWithClose
        open={availableVersion !== null}
        onClose={dismissUpdateAvailable}
        title={t('app-update-available-title')}
        forceDialog
      >
        <DialogContent>
          <DialogContentText>{t('app-update-available-text')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={dismissUpdateAvailable}>{t('button-later')}</Button>
          {storeUrl && (
            <Button variant="contained" onClick={() => openStore(storeUrl)}>
              {t('button-update-now')}
            </Button>
          )}
        </DialogActions>
      </DialogWrapperWithClose>
      {children}
    </React.Fragment>
  );
};

export default AppUpdateDialog;
